import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import type { Task, Stats } from '../types';

export default function Dashboard() {
  const { user } = useAuth();
  const [todayTasks, setTodayTasks] = useState<Task[]>([]);
  const [inProgressTasks, setInProgressTasks] = useState<Task[]>([]);
  const [overdueTasks, setOverdueTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, [user]);

  const loadDashboard = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      console.log('Dashboard loading for user:', user);

      // Determine user/manager filter
      const userIdFilter = user.role === 'employee' ? user.id : undefined;
      const managerIdFilter = user.role === 'manager' ? user.id : undefined;

      console.log('Stats request - role:', user.role, 'id:', user.id, 'managerIdFilter:', managerIdFilter);

      // Get today's tasks
      const today = new Date().toISOString().split('T')[0];
      const todayResponse = await api.getTasks({
        user_id: userIdFilter,
        manager_id: managerIdFilter,
        from: today,
        to: today,
        limit: 5,
      });
      setTodayTasks(todayResponse.tasks);

      // Get in-progress tasks
      const inProgressResponse = await api.getTasks({
        user_id: userIdFilter,
        manager_id: managerIdFilter,
        status: 'in_progress',
        limit: 5,
      });
      setInProgressTasks(inProgressResponse.tasks);

      // Get overdue tasks
      const overdueResponse = await api.getOverdueTasks({
        user_id: user.role === 'employee' ? user.id : undefined,
        manager_id: user.role === 'manager' ? user.id : undefined,
      });
      setOverdueTasks(overdueResponse.slice(0, 5));

      // Get stats - for today only
      if (user.role === 'employee') {
        console.log('Calling getEmployeeStats for user.id:', user.id);
        const statsResponse = await api.getEmployeeStats(user.id, 1);
        setStats(statsResponse);
      } else if (user.role === 'manager') {
        console.log('Calling getManagerStats for user.id:', user.id);
        const statsResponse = await api.getManagerStats(user.id, 1);
        setStats(statsResponse);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div>
      <h2 style={{ marginBottom: '1rem' }}>Добро пожаловать, {user?.first_name}!</h2>

      {/* Stats Cards */}
      {stats && (
        <div className="dashboard-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.total || (stats as any).total_tasks || 0}</div>
            <div className="stat-label">Всего задач</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.completed_pct}%</div>
            <div className="stat-label">Выполнено</div>
          </div>
          <div className="stat-card">
            <div className="stat-value" style={{ color: stats.overdue_count > 0 ? 'var(--danger)' : undefined }}>
              {stats.overdue_count}
            </div>
            <div className="stat-label">Просрочено</div>
          </div>
        </div>
      )}

      {/* Quick Links */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <Link to="/tasks?status=pending" className="btn btn-secondary">Новые</Link>
        <Link to="/tasks?status=in_progress" className="btn btn-secondary">В работе</Link>
        <Link to="/overdue" className="btn btn-secondary">Просроченные</Link>
      </div>

      {/* Today's Tasks */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Задачи на сегодня</h3>
          <Link to="/tasks" className="btn btn-secondary" style={{ fontSize: '0.75rem' }}>
            Все
          </Link>
        </div>
        {todayTasks.length === 0 ? (
          <p className="empty-state">Нет задач на сегодня</p>
        ) : (
          <div className="task-list">
            {todayTasks.map((task) => (
              <TaskCard key={task.id} task={task} />
            ))}
          </div>
        )}
      </div>

      {/* In Progress Tasks */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">В работе</h3>
        </div>
        {inProgressTasks.length === 0 ? (
          <p className="empty-state">Нет задач в работе</p>
        ) : (
          <div className="task-list">
            {inProgressTasks.map((task) => (
              <TaskCard key={task.id} task={task} />
            ))}
          </div>
        )}
      </div>

      {/* Overdue Tasks */}
      {overdueTasks.length > 0 && (
        <div className="card" style={{ borderLeft: '4px solid var(--danger)' }}>
          <div className="card-header">
            <h3 className="card-title" style={{ color: 'var(--danger)' }}>Просроченные</h3>
            <Link to="/overdue" className="btn btn-secondary" style={{ fontSize: '0.75rem' }}>
              Все
            </Link>
          </div>
          <div className="task-list">
            {overdueTasks.map((task) => (
              <TaskCard key={task.id} task={task} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TaskCard({ task }: { task: Task }) {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Link to={`/tasks/${task.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div className="task-item">
        <div className="task-header">
          <span className="task-title">{task.title}</span>
          <span className={`task-status status-${task.status}`}>
            {getStatusText(task.status)}
          </span>
        </div>
        {task.due_date && (
          <div className="task-meta">
            <span className="task-meta-item">
              📅 {formatDate(task.due_date)}
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}

function getStatusText(status: string): string {
  const map: Record<string, string> = {
    pending: 'Ожидает',
    in_progress: 'В работе',
    questions: 'Вопросы',
    completed: 'Выполнено',
    cancelled: 'Отменено',
  };
  return map[status] || status;
}
